import { atom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';

import {
  CustomQuizDetailDefinitionDto,
  Plan,
  PlanSection,
  PlanStep,
} from '@/_generated/api-clients/xp';
import { store } from '@/atoms';

/**
 * Union of Plan and Quiz fields needed by the shared translations modal.
 * Can't use either generated type alone — Plan has no quiz fields (`quizId`, `questions`)
 * and CustomQuizDetailDefinitionDto has no plan fields (`content.sections`, `content.steps`).
 * Callers cast back to their specific type in `onSave`.
 *
 * @todo Make the modal generic over the entity type so `onSave` returns the
 * caller's exact type (Plan or CustomQuizDetailDefinitionDto) without casting.
 */
export type ManageTranslationsState = Partial<Plan> &
  Partial<Pick<CustomQuizDetailDefinitionDto, 'questions' | 'quizId'>>;

export const manageTranslationsMutableAtom = atom(
  {} as ManageTranslationsState,
);

interface UpdatePayload {
  id: string;
  value?: string;
  targetLanguage: string;
}

type UpdateTaskPayload = UpdatePayload & {
  sectionId?: string;
};

type UpdateTaskUrlPayload = UpdateTaskPayload & {
  index: number;
};

interface UpdateQuestionPayload {
  value?: string;
  questionIndex: number;
  targetLanguage: string;
}

interface UpdateAnswerPayload {
  value?: string;
  questionIndex: number;
  answerIndex: number;
  targetLanguage: string;
}

const initializeTrainingPlanTranslations = (
  payload: ManageTranslationsState | undefined,
) => {
  const initializedState = initializeMissingProperties(payload);
  store.set(manageTranslationsMutableAtom, { ...initializedState });
};

const updatePlanNameTranslation = ({
  id,
  value,
  targetLanguage,
}: UpdatePayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    if (mutable.name && mutable.id === id) {
      mutable.name[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateSectionTranslation = ({
  id,
  value,
  targetLanguage,
}: UpdatePayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    const section = filterSection(mutable.content?.sections, id);
    if (section?.name) {
      section.name[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateTaskNameTranslation = ({
  id,
  value,
  sectionId,
  targetLanguage,
}: UpdateTaskPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    let steps = mutable.content?.steps;
    if (sectionId) {
      steps = filterSection(mutable.content?.sections, sectionId)?.steps;
    }
    const step = filterStep(steps, id);
    if (step?.name) {
      step.name[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateTaskNotesTranslation = ({
  id,
  value,
  sectionId,
  targetLanguage,
}: UpdateTaskPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    let steps = mutable.content?.steps;
    if (sectionId) {
      steps = filterSection(mutable.content?.sections, sectionId)?.steps;
    }
    const step = filterStep(steps, id);
    if (step?.note) {
      step.note[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateTaskUrlTranslation = ({
  id,
  value,
  sectionId,
  index,
  targetLanguage,
}: UpdateTaskUrlPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    let steps = mutable.content?.steps;
    if (sectionId) {
      steps = filterSection(mutable.content?.sections, sectionId)?.steps;
    }
    const step = filterStep(steps, id);
    if (step?.urls) {
      const url = step.urls.at(index)?.urlSet;
      if (url) {
        url[targetLanguage] = value ?? '';
      }
    }
    return mutable;
  });

const updateQuizNameTranslation = ({
  id,
  value,
  targetLanguage,
}: UpdatePayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    if ('quizId' in mutable && mutable.quizId === id && mutable.name) {
      mutable.name[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateQuestionTranslation = ({
  value,
  questionIndex,
  targetLanguage,
}: UpdateQuestionPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    const questions = mutable.questions;
    const questionDefinition = questions?.at(questionIndex);
    if (questionDefinition) {
      questionDefinition.question[targetLanguage] = value ?? '';
    }
    return mutable;
  });

const updateAnswerTranslation = ({
  value,
  questionIndex,
  answerIndex,
  targetLanguage,
}: UpdateAnswerPayload) =>
  store.set(manageTranslationsMutableAtom, prev => {
    const mutable = { ...prev };
    const answer = mutable.questions
      ?.at(questionIndex)
      ?.answers.at(answerIndex)?.answer;
    if (answer) {
      answer[targetLanguage] = value ?? '';
    }
    return mutable;
  });

export const actions = {
  initializeTrainingPlanTranslations,
  updatePlanNameTranslation,
  updateSectionTranslation,
  updateTaskNameTranslation,
  updateTaskNotesTranslation,
  updateTaskUrlTranslation,
  updateQuizNameTranslation,
  updateQuestionTranslation,
  updateAnswerTranslation,
};

export const initializeMissingProperties = (
  payload: ManageTranslationsState | undefined,
) => {
  const initializedState = cloneDeep(payload);

  if (!initializedState?.name?.es) {
    if (initializedState?.name) {
      initializedState.name.es = '';
    }
  }

  initializedState?.content?.steps?.map(step => {
    if (step.type !== 'task') {
      return;
    }

    step.name.es ||= '';
    if (step.note) {
      step.note.es ||= '';
    }
  });

  initializedState?.content?.sections?.map(section => {
    section.name.es ||= '';

    section.steps?.map(step => {
      if (step.type === 'task') {
        step.name.es ||= '';
        if (step.note) {
          step.note.es ||= '';
        }
      }
    });
  });

  return initializedState;
};

const filterSection = (
  sections: PlanSection[] | undefined,
  sectionId: string,
) => sections?.find(section => section.id === sectionId);

const filterStep = (steps: PlanStep[] | undefined, id: string) =>
  steps?.find(step => step.id === id);
